/**
 * mcgraw-hill: scripted answers for the AI desktop demo.
 *
 * When a McGraw Hill member asks one of these questions on the AI desktop (no
 * document open), the chat answers with the script instead of the model: the
 * same thinking steps, text and sources every time, streamed so it reads like
 * a live answer (mcgrawHillScriptedStream.ts). Every other question, and every
 * question in the document viewer, goes to the real AI.
 *
 * To add or edit one, change MCGRAW_HILL_SCRIPTED_ANSWERS below.
 */

import { mcgrawHillReferenceId } from './mcgrawHillReferences';

/** A source chip under the answer. */
export type McGrawHillScriptedSource =
  | {
      /**
       * A page of a chapter of the selected textbook: a chip that opens the
       * chapter's document at that page. The chapter is found by its number
       * ("Ch04.pdf" is 4) in the selected textbook, so the same script works
       * on any database.
       */
      kind: 'chapter';
      chapter: number;
      page: number;
      /** The chip's text, e.g. "Katzung · Ch. 4, p. 9". */
      label: string;
      /** Shown in the chip's preview, when it has one. */
      excerpt?: string;
    }
  | {
      /**
       * A page of a McGraw Hill reference document (the client PDFs and
       * articles the backend ingests): a chip that opens it in the viewer.
       * `documentId` is the document's fixed id from the backend config.
       */
      kind: 'document';
      documentId: string;
      /** The document's title, e.g. "Harrison's 22e, Chapter 265 …". */
      title: string;
      page: number;
      /** The section or table heading shown on the source row. */
      label: string;
      /** What opening the source highlights; the label when absent. */
      excerpt?: string;
    }
  | {
      /** Anything else (a journal article, a guideline): a plain label. */
      kind: 'reference';
      label: string;
    };

/** A reference figure shown inside the answer, placed with `[[figure:N]]`. */
export interface McGrawHillScriptedFigure {
  documentId: string;
  /** The document's title. */
  title: string;
  /** The figure's page ("Open chapter" goes there). */
  page: number;
  /** "FIGURE 265-3". */
  label: string;
  caption: string;
  /** The backend's reference figure id (mcgrawHillConfig.ts there). */
  figureId?: string;
}

/** A quote card shown inside the answer, like a live `quote_reference`. */
export interface McGrawHillScriptedQuote {
  /** The reference document's fixed id (backend config). */
  documentId: string;
  page: number;
  /** The document's display name on the card. */
  title: string;
  /** The passage, word for word from the page. */
  excerpt: string;
}

export interface McGrawHillScriptedAnswer {
  /**
   * Questions that trigger the script. Matching ignores case, punctuation and
   * extra spaces, but is otherwise exact: "What is A?" matches "what is a",
   * not "what is A in children?".
   */
  questions: readonly string[];
  /**
   * The thinking box's steps, in order, each shown for `ms` before the next.
   * The box ends as "Thought for Xs · N steps" with the total.
   */
  thinking: readonly { label: string; ms: number }[];
  /**
   * The answer, in Markdown (headings, lists, bold; links do not render). A
   * line `[[quote:N]]` places quote card N (0-based, from `quotes`) there.
   */
  answer: string;
  /** Quote cards the answer places with `[[quote:N]]`. */
  quotes?: readonly McGrawHillScriptedQuote[];
  /** Figure cards the answer places with `[[figure:N]]`. */
  figures?: readonly McGrawHillScriptedFigure[];
  /**
   * The action row the answer places with `[[actions]]`: a button opening a
   * reference document at a page, and Copy answer.
   */
  actions?: {
    open?: {
      label: string;
      documentId: string;
      page: number;
      highlightText?: string;
    };
  };
  sources: readonly McGrawHillScriptedSource[];
}

/**
 * The reference documents scripted answers cite: the backend's fixed ids
 * (mcgrawHillConfig.ts there) and titles.
 */
const HARRISONS_265 = {
  documentId: '373e321b-dcba-4d28-8815-ec730f73f82d',
  title: "Harrison's 22e, Chapter 265 Heart Failure: Management",
} as const;

const HARRISONS_416 = {
  documentId: '466bbafe-5dd9-45fd-953b-be7befab34a5',
  title:
    "Harrison's 22e, Chapter 416 Diabetes Mellitus: Management and Therapies",
} as const;

/** EMPULSE (Nature Medicine 2022), one of the open-access articles. */
const EMPULSE = {
  documentId: mcgrawHillReferenceId('S6'),
  title:
    'Nature Medicine 2022, The SGLT2 inhibitor empagliflozin in patients hospitalized for acute heart failure: a multinational randomized trial',
} as const;

// The pages the ADHF answer cites.
const CLINICAL_GUIDING_PRINCIPLES_PAGE = 5;
const EMPULSE_ABSTRACT_PAGE = 1;

/**
 * The demo's questions in presenting order; the desktop's demo dots fill the
 * composer with them (McGrawHillDemoDots).
 */
export const MCGRAW_HILL_DEMO_QUESTIONS = [
  '68M HFrEF EF 30% on ARNI, carvedilol, spironolactone, no DM. Add SGLT2i?',
  'Same pt, now admitted w/ ADHF. Start it inpatient or wait for clinic?',
] as const;

export const MCGRAW_HILL_SCRIPTED_ANSWERS: readonly McGrawHillScriptedAnswer[] =
  [
    {
      // The inpatient-start follow-up (Harrison's Ch. 265 and EMPULSE).
      questions: [MCGRAW_HILL_DEMO_QUESTIONS[1]],
      thinking: [
        { label: 'Thinking', ms: 900 },
        { label: "Reading Harrison's Chapter 265", ms: 1400 },
        { label: 'Reading Nature Medicine 2022 (EMPULSE)', ms: 1300 },
      ],
      answer: [
        `**Start it in hospital once he's stable, before discharge.** [[cite:265:${CLINICAL_GUIDING_PRINCIPLES_PAGE}:CLINICAL GUIDING PRINCIPLES]]`,
        '',
        '[[quote:0]]',
        '',
        `In EMPULSE, 530 patients admitted with acute heart failure, de novo or decompensated, started empagliflozin 10 mg daily a median of 3 days after admission. More had clinical benefit at 90 days than on placebo, win ratio 1.36, the same regardless of EF or diabetes, and serious adverse events were fewer on the drug. [[cite:S6:${EMPULSE_ABSTRACT_PAGE}:Abstract]]`,
        '',
        `Starting guideline therapy before discharge also goes with better adherence afterward, and in STRONG-HF rapid uptitration with close follow-up cut death and readmission at 180 days. [[cite:265:${CLINICAL_GUIDING_PRINCIPLES_PAGE}:CLINICAL GUIDING PRINCIPLES]]`,
        '',
        '[[actions]]',
      ].join('\n'),
      quotes: [
        {
          ...EMPULSE,
          page: EMPULSE_ABSTRACT_PAGE,
          excerpt:
            'In this double-blind trial (EMPULSE; NCT04157751), 530 patients with a primary diagnosis of acute de novo or decompensated chronic heart failure regardless of left ventricular ejection fraction were randomly assigned to receive empagliflozin 10 mg once daily or placebo. Patients were randomized in-hospital when clinically stable (median time from hospital admission to randomization, 3 days) and were treated for up to 90 days. … More patients treated with empagliflozin had clinical benefit compared with placebo (stratified win ratio, 1.36; 95% confidence interval, 1.09–1.68; P = 0.0054), meeting the primary endpoint. Clinical benefit was observed for both acute de novo and decompensated chronic heart failure and was observed regardless of ejection fraction or the presence or absence of diabetes.',
        },
      ],
      actions: {
        open: {
          label: "Open in Harrison's",
          documentId: HARRISONS_265.documentId,
          page: CLINICAL_GUIDING_PRINCIPLES_PAGE,
          highlightText: 'CLINICAL GUIDING PRINCIPLES',
        },
      },
      sources: [
        {
          kind: 'document',
          ...HARRISONS_265,
          page: CLINICAL_GUIDING_PRINCIPLES_PAGE,
          label: 'CLINICAL GUIDING PRINCIPLES',
        },
        {
          kind: 'document',
          ...EMPULSE,
          page: EMPULSE_ABSTRACT_PAGE,
          label: 'Abstract',
          // Opening it highlights the trial's design, as the quote shows.
          excerpt:
            'In this double-blind trial (EMPULSE; NCT04157751), 530 patients with a primary diagnosis of acute de novo or decompensated chronic heart failure',
        },
      ],
    },
    {
      // The SGLT-2 inhibitor demo question (Harrison's Ch. 265 and 416).
      questions: [MCGRAW_HILL_DEMO_QUESTIONS[0]],
      thinking: [
        { label: 'Thinking', ms: 900 },
        { label: "Reading Harrison's Chapter 265", ms: 1500 },
        { label: "Reading Harrison's Chapter 416", ms: 1200 },
      ],
      answer: [
        '**Yes. SGLT2 inhibitors are foundational HFrEF therapy alongside ARNI, beta blocker and MRA, with or without diabetes.** [[cite:265:20:SGLT-2 INHIBITION]][[cite:265:2:CLINICAL TRIALS IN HFpEF]]',
        '',
        '[[figure:0]]',
        '',
        'Dapagliflozin 10 mg daily or empagliflozin 10 mg daily, no titration. [[cite:265:18:TABLE 265-2]] DAPA-HF (4,744 patients) cut worsening HF or CV death 26% and EMPEROR-Reduced (3,730) cut CV death or HF hospitalization 25%, with the same effect in patients without diabetes. [[cite:265:20:SGLT-2 INHIBITION]]',
        '',
        'Expect a small early dip in eGFR and a mild diuretic effect, and counsel on genital mycotic infections. [[cite:416:18:SGLT-2 INHIBITORS]] Hold the drug during acute illness or before surgery for euglycemic ketoacidosis risk. [[cite:416:18:SGLT-2 INHIBITORS]][[cite:416:14:TABLE 416-6]]',
      ].join('\n'),
      figures: [
        {
          ...HARRISONS_265,
          page: 15,
          label: 'FIGURE 265-3',
          figureId: 'figure-265-3',
          caption:
            'Progressive decline in mortality with angiotensin-converting enzyme (ACE) inhibitors (ACEIs) or angiotensin receptor blockers (ARBs), angiotensin receptor-neprilysin inhibitors (ARNIs), beta blockers, mineralocorticoid receptor antagonists, sodium-glucose cotransporter-2 (SGLT-2) inhibitors, and balanced vasodilators (*selected populations such as African Americans). Addition of selected therapies (ivabradine, vericiguat) may further reduce heart failure (HF) hospitalization but does not substantially impact mortality. Further stack-on neurohormonal therapy is ineffective or results in worse outcome. Management of comorbidity (e.g., iron deficiency, sleep apnea) is of unproven efficacy. HFrEF, heart failure with reduced ejection fraction; PUFA, polyunsaturated fatty acid; SSRI, selective serotonin reuptake inhibitor.',
        },
      ],
      sources: [
        {
          kind: 'document',
          ...HARRISONS_265,
          page: 20,
          label: 'SGLT-2 INHIBITION',
        },
        {
          kind: 'document',
          ...HARRISONS_265,
          page: 2,
          label: 'CLINICAL TRIALS IN HFpEF',
        },
        {
          kind: 'document',
          ...HARRISONS_265,
          page: 18,
          label:
            'TABLE 265-2, Guideline-Directed Pharmacologic Therapy and Target Doses in Heart Failure with Reduced Ejection Fraction',
        },
        {
          kind: 'document',
          ...HARRISONS_416,
          page: 18,
          label: 'SGLT-2 INHIBITORS',
        },
        {
          kind: 'document',
          ...HARRISONS_416,
          page: 14,
          label:
            'TABLE 416-6, Agents Used for Treatment of Type 1 or Type 2 Diabetes',
        },
      ],
    },
    {
      // PLACEHOLDER until the real question, answer and sources arrive. The
      // trigger is deliberately one nobody asks by accident.
      questions: ['Show me the scripted demo answer'],
      thinking: [
        { label: 'Thinking', ms: 900 },
        {
          label: 'Searching Katzung’s Basic & Clinical Pharmacology',
          ms: 1400,
        },
        { label: 'Reading Chapter 4', ms: 1200 },
      ],
      answer: [
        'This is the **scripted demo answer**. It streams in like a live answer, with the same thinking steps and sources every time.',
        '',
        '- Replace this text with the real answer.',
        '- Point the sources at the right chapters and pages.',
      ].join('\n'),
      sources: [
        {
          kind: 'chapter',
          chapter: 4,
          page: 9,
          label: 'Katzung · Ch. 4, p. 9',
        },
        {
          kind: 'reference',
          label: 'Example reference · Journal, 2025',
        },
      ],
    },
  ];

const normalize = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** The script a question triggers, if any. */
export const findMcGrawHillScriptedAnswer = (
  message: string,
  scripts: readonly McGrawHillScriptedAnswer[] = MCGRAW_HILL_SCRIPTED_ANSWERS
): McGrawHillScriptedAnswer | null => {
  const asked = normalize(message);
  if (!asked) return null;

  return (
    scripts.find((script) =>
      script.questions.some((question) => normalize(question) === asked)
    ) ?? null
  );
};
